import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Simple types for our backend API
interface Subscription {
  id: string;
  userId: string;
  tier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED' | 'UNPAID';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  createdAt: string;
  updatedAt: string;
}

export type LimitedAction = 'project' | 'recipe' | 'report' | 'customSalt' | 'waterProfile' | 'formulation';

/** Counted by the server from the database (GET /subscription/status). */
export type Usage = Record<LimitedAction, number>;

/** Mirrors server/src/subscription/tiers.ts; the values come from the API, never from the client. */
export interface TierLimits {
  projectsTotal: number;
  recipesPerMonth: number;
  reportsPerMonth: number;
  customSaltsTotal: number;
  waterProfilesTotal: number;
  formulationsTotal: number;
}

export interface PlanFeatures {
  flahax: boolean;
  historyAnalysis: boolean;
}

export interface TrialStatus {
  daysLeft: number | null;
  requested: boolean;
  used: boolean;
}

// Import API configuration
import { API_BASE_URL } from '../../config/api';

interface SubscriptionState {
  subscription: Subscription | null;
  usage: Usage | null;
  limits: TierLimits | null;
  features: PlanFeatures | null;
  trial: TrialStatus | null;
  enforcement: 'off' | 'warn' | 'block' | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: SubscriptionState = {
  subscription: null,
  usage: null,
  limits: null,
  features: null,
  trial: null,
  enforcement: null,
  isLoading: false,
  error: null
};

// Helper function to get auth token
const getAuthToken = (getState: any): string | null => {
  const state = getState();
  return state.auth?.token || null;
};

// Async thunks
export const fetchSubscription = createAsyncThunk(
  'subscription/fetchSubscription',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to fetch subscription');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const startTrial = createAsyncThunk(
  'subscription/startTrial',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/start-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to start trial');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const checkUsageLimit = createAsyncThunk(
  'subscription/checkUsageLimit',
  async (resourceType: string, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/check-limit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ action: resourceType }),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to check usage limit');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const checkFeatureAccess = createAsyncThunk(
  'subscription/checkFeatureAccess',
  async (feature: string, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/check-feature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ feature }),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to check feature access');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const initiateUpgrade = createAsyncThunk(
  'subscription/initiateUpgrade',
  async (upgradeData: { targetTier: string }, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(upgradeData),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to initiate upgrade');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to cancel subscription');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Subscription slice
const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch subscription
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload.subscription;
        state.usage = action.payload.usage;
        state.limits = action.payload.limits;
        state.features = action.payload.features;
        state.trial = action.payload.trial;
        state.enforcement = action.payload.enforcement;
        state.error = null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Start trial
    builder
      .addCase(startTrial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startTrial.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload.subscription;
        state.error = null;
      })
      .addCase(startTrial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Cancel subscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.subscription) {
          state.subscription.cancelAtPeriodEnd = true;
        }
        state.error = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
